import { type QueryClient } from '@tanstack/react-query'
import {
	createRootRouteWithContext,
	HeadContent,
	Outlet,
	ScriptOnce,
	Scripts,
} from '@tanstack/react-router'
import { NavigationProgress } from '@/components/navigation-progress'
import TanstackDevTools from '@/components/tanstack-dev-tools'
import { Toaster } from '@/components/ui/sonner'
import GeneralError from '@/features/errors/general-error'
import NotFoundError from '@/features/errors/not-found-error'
import ContextProvider from '@/provider'
import appCss from '~/lib/styles/app.css?url'

export const Route = createRootRouteWithContext<{
	queryClient: QueryClient
}>()({
	head: () => ({
		meta: [
			{
				charSet: 'utf-8',
			},
			{
				name: 'viewport',
				content: 'width=device-width, initial-scale=1',
			},
			{
				title: 'GaiaDEX',
			},
			{
				name: 'description',
				content: 'Trade on the GaiaDEX',
			},
		],
		links: [
			{
				rel: 'stylesheet',
				href: appCss,
			},
			{
				rel: 'icon',
				type: 'image/svg+xml',
				href: '/favicon.svg',
			},
		],
	}),
	component: RootComponent,
	notFoundComponent: NotFoundError,
	errorComponent: GeneralError,
})

function RootComponent() {
	return (
		<RootDocument>
			<Outlet />
		</RootDocument>
	)
}

function RootDocument({ children }: { readonly children: React.ReactNode }) {
	return (
		// suppress since we're updating the "dark" class in a custom script below
		<html suppressHydrationWarning>
			<head>
				<HeadContent />
			</head>
			<body>
				<ScriptOnce>
					{`document.documentElement.classList.toggle(
			'light',
			localStorage.theme === 'light' || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: light)').matches)
			)`}
				</ScriptOnce>
				<NavigationProgress />
				<Toaster richColors duration={3000} />

				<ContextProvider>{children}</ContextProvider>

				{import.meta.env.DEV && <TanstackDevTools />}

				<Scripts />
			</body>
		</html>
	)
}
